import React, { useEffect, useState } from 'react';

import AssetService from '../../../shared/services/AssetService';
import Button from '@mui/material/Button';
import { CarouselComponent } from '../../models/LessonComponent';
import CarouselIndicators from './CarouselIndicators';
import { CarouselItem } from '../../models/LessonComponentContent';
import CarouselItemViewer from './CarouselItemViewer';
import ChangeFile from '../../../shared/components/ChangeFile';
import ConfirmationDialog from '../../../shared/components/ConfirmationDialog';
import Constants from '../../constants';
import DefaultImage from '../../images/default-image.jpg';
import { DropResult } from 'react-beautiful-dnd';
import Grid from '@mui/material/Unstable_Grid2';
import ItemEditor from './ItemEditor';
import ItemList from './ItemList';
import { ItemModel } from '../../models/ItemModel';
import LessonComponentService from '../../services/LessonComponentService';
import NewComponentService from '../../services/NewComponentService';
import VisitedIcon from '../../images/visited.svg';
import { cloneDeep } from 'lodash';
import styles from '../../styles/Carousel.module.css';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonEditContext } from '../../models/LessonEditContext';
import { useSwipeable } from 'react-swipeable';

interface CarouselProps {
  component: CarouselComponent;
  onSubmit: (value: string) => void; //* The 'value' is the visited items id as JSON string.
}

export default function Carousel(props: CarouselProps) {
  const carousels = props.component.content.carousel;
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const currentItem = carousels[currentIndex];
  const [deleteItem, setDeleteItem] = useState(false);

  let visited: ItemModel[] =
    (props.component.answer && JSON.parse(props.component.answer)) || [];

  const { mode } = useLessonContext();
  const numItems = carousels.length || 0;

  const { handleSettingsChange } = useLessonEditContext();

  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => handleShowNextItem(),
    onSwipedRight: () => handleShowPreviousItem(),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const hasTitle = carousels.some(
    (c) => LessonComponentService.getFilteredContent(c.heading) !== ''
  );

  useEffect(() => {
    if (
      !visited.find((i) => i.itemId === currentItem.id) &&
      LessonComponentService.canSubmit(mode)
    ) {
      props.onSubmit(JSON.stringify([...visited, { itemId: currentItem.id }]));
    }
  }, [currentItem]);

  const handleHeadingChange = (value: string) => {
    const copy = cloneDeep(props.component.content.carousel);
    copy[currentIndex].heading = value;
    handleChange(copy);
  };

  const handleTextChange = (value: string) => {
    const copy = cloneDeep(props.component.content.carousel);
    copy[currentIndex].body = value;
    handleChange(copy);
  };

  const handleChange = (items: CarouselItem[]) => {
    const copy = cloneDeep(props.component);
    copy.content.carousel = items;
    handleSettingsChange(copy);
  };

  const handleAssetSelect = (assetkey: string) => {
    const copy = cloneDeep(props.component.content.carousel);
    copy[currentIndex].image = assetkey;
    handleChange(copy);
  };

  const handleShowPreviousItem = () => {
    setCurrentIndex((current) => {
      return current === 0 ? numItems - 1 : current - 1;
    });
  };

  const handleShowNextItem = () => {
    setCurrentIndex((current) => {
      return current === numItems - 1 ? 0 : current + 1;
    });
  };

  const handleAdd = () => {
    const newItem = NewComponentService.createCarousel();
    const newIndex = currentIndex + 1;
    const copy = cloneDeep(props.component.content.carousel);
    copy.splice(newIndex, 0, newItem);
    handleChange(copy);
    setCurrentIndex(newIndex);
  };

  const handleDelete = (id?: string) => {
    const copy = cloneDeep(props.component.content.carousel);
    let index: number = null;
    index = id ? copy.findIndex((i) => i.id === id) : currentIndex;
    if (index === 0 && copy.length === 1) {
      copy[index] = NewComponentService.createCarousel();
      handleChange(copy);
    } else {
      handleChange(copy.filter((i, idx) => idx !== index));
      if (index !== 0) setCurrentIndex(index - 1);
      else setCurrentIndex(index);
    }
  };

  const resetItems = () => {
    return (
      carousels.length === 1 &&
      carousels[0].image === '' &&
      LessonComponentService.getFilteredContent(carousels[0].heading) === '' &&
      LessonComponentService.getFilteredContent(carousels[0].body) === ''
    );
  };

  const handleSelectItem = (index: number) => {
    setCurrentIndex(index);
  };

  const isVisited = () => {
    return visited?.map(({ itemId }) => itemId).includes(currentItem.id);
  };

  const handleDragEnd = (result: DropResult) => {
    // prevent from destination index outbound error
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const targetIndex = result.destination.index;
    const copy = cloneDeep(carousels);
    const item = copy[sourceIndex];
    copy.splice(sourceIndex, 1);
    copy.splice(targetIndex, 0, item);
    handleChange(copy);
  };

  return (
    <>
      <div className={styles.imageContainer} {...swipeableHandlers}>
        <img
          className={styles.image}
          src={AssetService.getAssetUrl(currentItem?.image) || DefaultImage}
        />
        {mode === 'edit' && (
          <ChangeFile
            assetkey={currentItem?.image}
            assetTypes={['image']}
            handleSelect={handleAssetSelect}
          />
        )}
        {mode === 'mark' && isVisited() && (
          /* Show visited tick only in the marker view */
          <div className={`${styles.visitedPosition}`}>
            <img src={VisitedIcon} alt="visited" />
          </div>
        )}
      </div>

      <Grid
        container
        direction="column"
        alignItems="center"
        spacing={3}
        className={styles.itemText}
      >
        {mode !== 'edit' ? (
          <CarouselItemViewer items={carousels} currentIndex={currentIndex} />
        ) : (
          <>
            <Grid style={{ width: '100%' }}>
              <ItemEditor
                componentkey={props.component.componentkey}
                value={currentItem?.heading}
                styleType="h2"
                placeholder="Click to enter title"
                onChange={handleHeadingChange}
                isCenterAligned
              />
            </Grid>
            <Grid style={{ width: '100%', marginTop: '6px' }}>
              <ItemEditor
                componentkey={props.component.componentkey}
                value={currentItem?.body}
                styleType="p"
                placeholder="Click to enter text"
                onChange={handleTextChange}
                isCenterAligned
              />
            </Grid>
          </>
        )}
        <Grid style={{ minWidth: '60%' }}>
          <CarouselIndicators
            currentIndex={currentIndex}
            carousels={carousels}
            onShowPreviousItem={handleShowPreviousItem}
            onSelectItem={handleSelectItem}
            onShowNextItem={handleShowNextItem}
          />
        </Grid>
        {mode === 'edit' && (
          <Grid>
            <Grid container spacing={2}>
              <Grid>
                <Button
                  variant="contained"
                  className={styles.buttonSize}
                  color="primary"
                  onClick={() => setDeleteItem(true)}
                  disabled={resetItems()}
                >
                  Delete Item
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.buttonSize}
                  onClick={handleAdd}
                  disabled={carousels?.length === Constants.CarouselMaxNumItems}
                >
                  Add Item
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
      {deleteItem && (
        <ConfirmationDialog
          open
          title={'Are you sure you want to delete this item?'}
          message={''}
          onClose={async (agreed: boolean) => {
            setDeleteItem(false);
            if (!agreed) {
              return;
            }
            handleDelete();
          }}
        />
      )}
      {mode === 'edit' && carousels && hasTitle && (
        <ItemList
          items={carousels}
          onDragEnd={handleDragEnd}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}
